import {
    AppPlatform,
    AppSettings,
    AppStoreAvailableBuild,
    AppStoreBuildType,
    AppStoreSelectedBuildReason,
    BuildStatus,
    DestinationPhasedRelease,
    IntegrationType,
    Release,
    ReleaseStatus,
    ReleaseTimelinePhase,
    RolloutMonitoring,
    StabilityIssue,
    StatChangeStatus,
    Statistic,
    StatisticCategory,
    StatisticType,
    StatisticUnit,
} from '@/types';
import {
    ArchetypeGroupSummaryDetails,
    OverviewDestinationStatus,
    WorkflowOverview,
    WorkflowOverviewWorkflowRun,
    WorkflowRunOverviewDisplayStatus,
} from '@/types/workflowOverview';
import { mockPhasedReleaseForPlatform } from './phasedRelease';
import { Destination } from '@/types/destinations';
import { topNewStabilityIssues, topStabilityIssues } from './appHome';
import { buildStepConfigToDestinationsMap } from './workflows';
import { baseRolloutData } from './rollout';
import dayjs from 'dayjs';

const generateMockAppStoreBuildsForDestination = (
    destination: Destination,
    version: string,
): AppStoreAvailableBuild[] => {
    const buildNumber = Math.floor(Math.random() * 1000) + 100;
    const isApple = destination.platform === AppPlatform.Ios;
    const integrationId = isApple ? IntegrationType.Apple : IntegrationType.Google;

    return [
        {
            isRecentBuild: true,
            appStoreBuild: {
                buildNumber: buildNumber.toString(),
                id: `build-${buildNumber}`,
                integrationId,
                isProcessed: true,
                type: isApple ? AppStoreBuildType.IosBinary : AppStoreBuildType.AndroidAAB,
                uploadedAt: dayjs().subtract(2, 'hours').toISOString(),
            },
            associatedCIBuild: {
                buildHash: `hash-${buildNumber}`,
                commitUrl: 'https://github.com/demo/demo/commit/abc123',
                buildIdentifier: buildNumber.toString(),
                integrationId: IntegrationType.Bitrise,
                buildUrl: `https://app.bitrise.io/build/${buildNumber}`,
                buildStartedAt: dayjs().subtract(3, 'hours').toISOString(),
                buildFinishedAt: dayjs().subtract(2, 'hours').toISOString(),
                buildStatus: BuildStatus.Success,
                providerBuildStatusString: 'Success',
                commit: 'abc123def456',
                commitMessage: `Build for ${version}`,
                commitAuthor: 'runway-bot',
                artifacts: [],
            },
            isCurrentlySelected: true,
            isNewestAppStoreBuild: true,
            stabilityInfos: {
                'sentry:xid': {
                    version,
                    buildIdentifier: buildNumber.toString(),
                    appStoreAppIdentifier: '',
                    integrationId: IntegrationType.Sentry,
                    sessionStability: 99.5 + Math.random() * 0.5,
                    userStability: 99.3 + Math.random() * 0.7,
                    sessionAdoption: Math.random() * 10,
                    userAdoption: Math.random() * 10,
                    adoptionPeriodString: '24-48H',
                    url: 'https://sentry.io',
                    environmentName: 'production',
                    topStabilityIssues: topStabilityIssues(destination.platform) as StabilityIssue[],
                    topNewStabilityIssues: topNewStabilityIssues(destination.platform) as StabilityIssue[],
                    topStabilityIssuesNonFatal: [],
                    topNewStabilityIssuesNonFatal: [],
                },
            },
            selectedBuildReason: AppStoreSelectedBuildReason.AutoSelection,
        },
    ];
};

const generateMockStatsForRelease = (): Statistic[] => {
    const baseFields = {
        changeStatus: StatChangeStatus.Neutral,
        unit: StatisticUnit.Count,
        additionalData: undefined,
        category: StatisticCategory.Work,
    };

    return [
        {
            ...baseFields,
            value: Math.floor(Math.random() * 200) + 50,
            type: StatisticType.LinesOfCodeChanged,
            average: 7371,
            change: Math.floor(Math.random() * 1000) - 500,
        },
        {
            ...baseFields,
            value: Math.floor(Math.random() * 30) + 5,
            type: StatisticType.CommitCount,
            average: 45,
            change: Math.floor(Math.random() * 20) - 10,
        },
        {
            ...baseFields,
            value: Math.floor(Math.random() * 10) + 2,
            type: StatisticType.FixesCount,
            average: 8,
            change: Math.floor(Math.random() * 5) - 2,
        },
    ];
};

const generateStepRunToDestinationMap = (
    release: Release,
    workflowStepGraph: { stepGroups: any[] },
): Record<string, string[]> => {
    const map: Record<string, string[]> = {};
    const stepConfigToDestinations = buildStepConfigToDestinationsMap(workflowStepGraph);

    release.stepRunGraph.stepGroups.forEach((stepGroup) => {
        stepGroup.stepArchetypeGroups.forEach((archetypeGroup) => {
            archetypeGroup.stepRunStatusDatum.forEach((stepRunStatus) => {
                if (stepRunStatus.stepRunId && stepRunStatus.stepConfigId) {
                    const destinationIds = stepConfigToDestinations[stepRunStatus.stepConfigId] || [];
                    map[stepRunStatus.stepRunId] = destinationIds;
                }
            });
        });
    });

    return map;
};

export const mockWorkflowOverview = (
    appId: string,
    workflowId: string,
    appSettings: AppSettings,
    releases: Release[],
): WorkflowOverview => {
    const { destinationInfoByDestinationStepConfigId, destinations, stepGraph } = appSettings;
    const allDestinationStepIds = Object.keys(destinationInfoByDestinationStepConfigId);
    return {
        appId,
        workflowId,
        selectedDestinationStepIds: allDestinationStepIds,
        workflowDestinationStepIds: allDestinationStepIds,
        destinationInfoByDestinationStepId: destinationInfoByDestinationStepConfigId,
        workflowRuns: releases.map((release) => {
            const isRollingOut =
                release.timelinePhase === ReleaseTimelinePhase.Completed &&
                (release.destinationPhasedReleases?.length ?? 0) > 0;
            return mockWorkflowOverviewWorkflowRun(appId, workflowId, destinations, release, isRollingOut, stepGraph);
        }),
    };
};

export const mockWorkflowOverviewWorkflowRun = (
    appId: string,
    workflowId: string,
    destinations: Destination[],
    release: Release,
    isRollingOut: boolean = false,
    stepGraph?: { stepGroups: any[] },
): WorkflowOverviewWorkflowRun => {
    const destinationStatusByDestinationStepId = destinations.reduce(
        (acc, destination) => {
            acc[destination.stepConfigId] = OverviewDestinationStatus.Approved;
            return acc;
        },
        {} as Record<string, OverviewDestinationStatus>,
    );

    let overviewDisplayStatus = WorkflowRunOverviewDisplayStatus.Completed;
    if (release.timelinePhase === ReleaseTimelinePhase.Upcoming) {
        overviewDisplayStatus = WorkflowRunOverviewDisplayStatus.Upcoming;
    } else if (release.timelinePhase === ReleaseTimelinePhase.Current) {
        overviewDisplayStatus = WorkflowRunOverviewDisplayStatus.Active;
    } else if (isRollingOut) {
        overviewDisplayStatus = WorkflowRunOverviewDisplayStatus.RollingOut;
    } else if (release.status === ReleaseStatus.Skipped) {
        overviewDisplayStatus = WorkflowRunOverviewDisplayStatus.Skipped;
    }

    const phasedReleaseWidgetGroups: DestinationPhasedRelease[][] = [];
    const phasedReleaseInfosByDestinationStepId: Record<string, DestinationPhasedRelease> = {};
    if (isRollingOut) {
        for (const destination of destinations) {
            // Stores without a day-scheduled phased release (e.g. Expo EAS) yield no phased release API
            const phasedRelease = mockPhasedReleaseForPlatform(destination.platform, release.releasedAt!);
            if (!phasedRelease) {
                continue;
            }
            const destinationPhasedRelease: DestinationPhasedRelease = {
                ...phasedRelease,
                destination,
                destinationStepConfigId: destination.stepConfigId,
                daysToPercentSchedule: [0, 10, 20, 50, 100],
                isAutomatedPhasedRollout: true,
                isPaused: false,
                isAccelerated: false,
                isCompleted: false,
            } as DestinationPhasedRelease;
            phasedReleaseWidgetGroups.push([destinationPhasedRelease]);
            phasedReleaseInfosByDestinationStepId[destination.stepConfigId] = destinationPhasedRelease;
        }
    }

    // Randomize timeline events
    const keepRatio = 0.6 + Math.random() * 0.4;
    const numEventsToKeep = Math.floor(release.timelineEventsHistory!.length * keepRatio);
    const shuffledEvents = [...release.timelineEventsHistory!].sort(() => Math.random() - 0.5);
    const randomizedEvents = shuffledEvents.slice(0, numEventsToKeep);

    // Generate random but valid feature step data
    const totalItemsCount = Math.floor(Math.random() * 30) + 10; // 10-39 items
    const completedItemsCount = Math.floor(Math.random() * (totalItemsCount - 5)) + 5; // 5 to totalItemsCount-1
    const percentFeatureComplete = Math.round((completedItemsCount / totalItemsCount) * 100);

    const featuresStep = {
        completedItemsCount,
        pendingItemsCount: 0,
        totalItemsCount,
        percentFeatureComplete,
    };

    return {
        ...release,
        appId,
        workflowId,
        workflowRunId: `${release.releaseId}-run`,
        overviewDisplayStatus,
        events: randomizedEvents,
        destinationStatusByDestinationStepId: destinationStatusByDestinationStepId,
        phasedReleaseInfosByDestinationStepId,
        startedReviewAt: null,
        targetKickoffDate: release.targetKickoffDate ?? '',
        targetSubmitDate: release.targetSubmitDate ?? '',
        targetReleaseDate: release.targetReleaseDate ?? '',
        skippedAt: release.skippedAt ?? '',
        kickedOffAt: release.kickedOffAt ?? '',
        submittedAt: release.submittedAt ?? '',
        releasedAt: release.releasedAt ?? '',
        completedAt: release.releasedAt,
        releasePilotId: release.releasePilotId ?? '',
        workflowSummaryMessage: release.releaseSummaryMessage?.message,
        workflowPlanningSummary: release.releaseSummaryMessage?.message,
        submittedAtByDestinationStepId:
            release.submittedAt ?
                destinations.reduce(
                    (acc, dest) => {
                        acc[dest.stepConfigId] = release.submittedAt!;
                        return acc;
                    },
                    {} as Record<string, string>,
                )
            :   {},
        startedReviewAtByDestinationStepId:
            release.submittedAt ?
                destinations.reduce(
                    (acc, dest) => {
                        // Review starts a few hours after submission
                        acc[dest.stepConfigId] = dayjs(release.submittedAt).add(2, 'hours').toISOString();
                        return acc;
                    },
                    {} as Record<string, string>,
                )
            :   {},
        releasedAtByDestinationStepId:
            release.releasedAt ?
                destinations.reduce(
                    (acc, dest) => {
                        acc[dest.stepConfigId] = release.releasedAt!;
                        return acc;
                    },
                    {} as Record<string, string>,
                )
            :   {},
        archetypeGroupIdToGroupDetailsMap: release.stepRunGraph.stepGroups.reduce(
            (map, stepGroup, groupIndex) => {
                stepGroup.stepArchetypeGroups.forEach((archetypeGroup) => {
                    map[archetypeGroup.id] = {
                        parentStepGroupIndex: groupIndex,
                        archetype: archetypeGroup.archetype,
                        archetypeGroupId: archetypeGroup.id,
                        archetypeGroupStepConfigIds: archetypeGroup.stepRunStatusDatum.map(
                            (step) => step.stepConfigId || '',
                        ),
                        archetypeGroupStepRunIds: archetypeGroup.stepRunStatusDatum.map((step) => step.stepRunId || ''),
                    };
                });
                return map;
            },
            {} as Record<string, ArchetypeGroupSummaryDetails>,
        ),
        stepRunIdToDestinationStepConfigIdsMap: stepGraph ? generateStepRunToDestinationMap(release, stepGraph) : {},
        rolloutMonitoringByDestinationStepId:
            isRollingOut ?
                destinations.reduce(
                    (acc, dest) => {
                        acc[dest.stepConfigId] = baseRolloutData(dest.platform) as RolloutMonitoring;
                        return acc;
                    },
                    {} as Record<string, RolloutMonitoring>,
                )
            :   {},
        selectedAppStoreBuildsByDestinationStepId: destinations.reduce(
            (acc, dest) => {
                acc[dest.stepConfigId] = generateMockAppStoreBuildsForDestination(dest, release.version);
                return acc;
            },
            {} as Record<string, AppStoreAvailableBuild[]>,
        ),
        phasedReleaseWidgetGroups,
        stats: generateMockStatsForRelease(),
        featuresStep,
        lifecycleAutomations: null,
        workflowRunGraph: {
            legacyReleaseId: release.id,
            stepGroups: release.stepRunGraph.stepGroups,
        },
    };
};
